import { memo, useMemo } from 'react'
import Tippy from '@tippyjs/react'

import Pill from './Pill'

const SEASONALITY_LABELS = {
  avg_price: 'Ø Preis',
  base_value: 'Basiswert',
  index: 'Index',
  price_index: 'Preisindex',
  salesdrops: 'Salesdrops',
  is_high_season: 'Saisonalität',
  is_high_volume: 'Hohes Volumen',
}

const SEASONALITY_KEY_ORDER = ['avg_price', 'base_value', 'index', 'price_index', 'salesdrops', 'is_high_season', 'is_high_volume']

const MONTH_LABELS = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez']

const formatSeasonalityPeriod = (period) => {
  if (typeof period !== 'string' || !period.includes('-')) {
    return period || '–'
  }

  const [year, month] = period.split('-')
  const index = Number(month) - 1
  if (!year || Number.isNaN(index) || index < 0 || index > 11) {
    return period
  }

  return `${MONTH_LABELS[index]} ${year}`
}

const formatSeasonalityKey = (key, baseLabel) => {
  if (key === 'base_value' && baseLabel) {
    return baseLabel
  }
  return SEASONALITY_LABELS[key] || key
}

const formatSeasonalityValue = (value, key) => {
  if (value === null || value === undefined || value === '') {
    return '–'
  }

  if (typeof value === 'boolean') {
    return value ? 'Ja' : 'Nein'
  }

  if (typeof value === 'number') {
    let options
    if (key === 'index' || key === 'price_index') {
      options = { minimumFractionDigits: 2, maximumFractionDigits: 2 }
    } else if (Number.isInteger(value)) {
      options = { maximumFractionDigits: 0 }
    } else {
      options = { minimumFractionDigits: 2, maximumFractionDigits: 2 }
    }
    return value.toLocaleString('de-DE', options)
  }

  return String(value)
}

const parseSeasonalityData = (seasonality) => {
  if (!seasonality) {
    return []
  }

  const normalizeEntry = (period, metrics) => {
    if (!metrics || typeof metrics !== 'object') {
      return null
    }

    const metricEntries = Object.entries(metrics).filter(([key, val]) => key !== 'month' && val !== null && val !== undefined && val !== '')
    if (!metricEntries.length) {
      return null
    }

    const resolvedPeriod = period || metrics.month
    if (!resolvedPeriod) {
      return null
    }

    return {
      period: resolvedPeriod,
      metrics: Object.fromEntries(metricEntries)
    }
  }

  if (Array.isArray(seasonality)) {
    return seasonality
      .map((entry) => {
        if (!entry || typeof entry !== 'object') {
          return null
        }
        const { month, ...rest } = entry
        return normalizeEntry(month, rest)
      })
      .filter(Boolean)
      .sort((a, b) => a.period.localeCompare(b.period))
  }

  if (typeof seasonality !== 'object') {
    return []
  }

  return Object.entries(seasonality)
    .map(([period, metrics]) => normalizeEntry(period, metrics))
    .filter(Boolean)
    .sort((a, b) => a.period.localeCompare(b.period))
}

const getSeasonalityPeriodMeta = (period) => {
  if (typeof period !== 'string') {
    return null
  }

  const [yearStr, monthStr] = period.split('-')
  const year = Number(yearStr)
  const month = Number(monthStr) - 1

  if (!Number.isInteger(year) || !Number.isInteger(month) || month < 0 || month > 11) {
    return null
  }

  return {
    year,
    month,
    index: year * 12 + month,
  }
}

const findSeasonalityStreak = (items) => {
  let best = null
  let current = null

  items.forEach(({ period, metrics }) => {
    const meta = getSeasonalityPeriodMeta(period)
    if (!meta) {
      current = null
      return
    }

    const isActive = Boolean(metrics?.is_high_season ?? metrics?.is_high_volume)

    if (!isActive) {
      if (current && (!best || current.length > best.length || (current.length === best.length && current.end.index > best.end.index))) {
        best = current
      }
      current = null
      return
    }

    if (!current) {
      current = { start: meta, end: meta, length: 1 }
      return
    }

    if (meta.index === current.end.index + 1) {
      current = { ...current, end: meta, length: current.length + 1 }
      return
    }

    if (!best || current.length > best.length || (current.length === best.length && current.end.index > best.end.index)) {
      best = current
    }

    current = { start: meta, end: meta, length: 1 }
  })

  if (current && (!best || current.length > best.length || (current.length === best.length && current.end.index > best.end.index))) {
    best = current
  }

  return best
}

const formatSeasonalityStreakLabel = (streak) => {
  if (!streak) {
    return null
  }

  const { start, end, length } = streak

  const startMonthLabel = MONTH_LABELS[start.month] ?? ''
  const endMonthLabel = MONTH_LABELS[end.month] ?? ''

  if (length <= 1 || start.index === end.index) {
    return startMonthLabel || null
  }

  if (start.year === end.year) {
    if (length === 2) {
      return `${startMonthLabel} & ${endMonthLabel}`.trim()
    }
    return `${startMonthLabel}-${endMonthLabel}`.trim()
  }

  return `${startMonthLabel} ${start.year} – ${endMonthLabel} ${end.year}`.trim()
}

const computeSeasonalityStreak = (datasets) => {
  for (const dataset of datasets) {
    const streak = findSeasonalityStreak(dataset.items)
    if (streak) {
      const label = formatSeasonalityStreakLabel(streak)
      if (label) {
        return label
      }
    }
  }
  return null
}

const toNumber = (value) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

const formatPriceValue = (value) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return null
  }
  return `${value.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}€`
}

export const computeVelocityPriceRange = (datasets) => {
  if (!Array.isArray(datasets) || !datasets.length) {
    return null
  }

  for (let datasetIndex = 0; datasetIndex < datasets.length; datasetIndex++) {
    const entry = datasets[datasetIndex]
    if (!entry || typeof entry !== 'object') {
      continue
    }

    const items = Array.isArray(entry.items) ? entry.items : parseSeasonalityData(entry.value)
    if (!items.length) {
      continue
    }

    const prices = []

    items.forEach(({ metrics }) => {
      if (!metrics) {
        return
      }

      const isVelocity = Boolean(metrics.is_high_volume)
      if (!isVelocity) {
        return
      }

      const price =
        toNumber(metrics.velocity_price ?? metrics.avg_price ?? metrics.base_value)

      if (price !== null) {
        prices.push(price)
      }
    })

    if (!prices.length) {
      continue
    }

    const minPrice = Math.min(...prices)
    const maxPrice = Math.max(...prices)

    const minLabel = formatPriceValue(minPrice)
    const maxLabel = formatPriceValue(maxPrice)

    if (!minLabel && !maxLabel) {
      continue
    }

    if (!maxLabel || minPrice === maxPrice) {
      return minLabel ?? maxLabel
    }

    if (!minLabel) {
      return maxLabel
    }

    return `${minLabel} - ${maxLabel}`
  }

  return null
}

export const createSeasonalityDatasets = (product, regionKey, regionData) => {
  const regionSeasonality = regionData?.seasonality || {}
  const datasetKeyPrefix = regionKey ?? 'global'

  return [
    {
      key: `${datasetKeyPrefix}-sales`,
      title: 'Seasonality Sales',
      baseLabel: 'Conf. Sales',
      value: regionData?.seasonality_sales ??
        regionSeasonality?.sales ??
        product.seasonality_sales ??
        product.seasonality?.sales ??
        null,
    },
    {
      key: `${datasetKeyPrefix}-bsr`,
      title: 'Seasonality BSR',
      baseLabel: 'BSR',
      value: regionData?.seasonality_bsr ??
        regionSeasonality?.bsr ??
        product.seasonality_bsr ??
        product.seasonality?.bsr ??
        null,
    },
    {
      key: `${datasetKeyPrefix}-bsrdrops`,
      title: 'Seasonality BSR Drops',
      baseLabel: 'Salesdrops',
      value: regionData?.seasonality_bsrdrops ??
        regionSeasonality?.bsrdrops ??
        regionSeasonality?.bsr_drops ??
        product.seasonality_bsrdrops ??
        product.seasonality?.bsrdrops ??
        product.seasonality?.bsr_drops ??
        null,
    },
  ]
}

export const resolveVelocityPrice = (datasets, regionData) => {
  const computed = computeVelocityPriceRange(datasets)
  if (computed !== null && computed !== undefined) {
    return computed
  }
  return regionData?.velocity_price ?? null
}

const normalizeSeasonalityDatasets = (datasets) => {
  if (!Array.isArray(datasets) || !datasets.length) {
    return []
  }

  return datasets
    .map((entry, index) => {
      if (!entry || typeof entry !== 'object') {
        return null
      }

      const items = parseSeasonalityData(entry.value)
      if (!items.length) {
        return null
      }

      return {
        key: entry.key ?? entry.title ?? `dataset-${index}`,
        title: entry.title,
        baseLabel: entry.baseLabel,
        items
      }
    })
    .filter(Boolean)
}

const SeasonalityTooltipSection = memo(function SeasonalityTooltipSection({ items, title, baseLabel }) {
  const metricKeys = useMemo(() => {
    const set = new Set()
    items.forEach(({ metrics }) => {
      Object.keys(metrics).forEach((key) => set.add(key))
    })
    return Array.from(set).sort((a, b) => {
      const indexA = SEASONALITY_KEY_ORDER.indexOf(a)
      const indexB = SEASONALITY_KEY_ORDER.indexOf(b)
      if (indexA === -1 && indexB === -1) {
        return a.localeCompare(b)
      }
      if (indexA === -1) return 1
      if (indexB === -1) return -1
      return indexA - indexB
    })
  }, [items])

  return (
    <>
      {title && (
        <div className='uppercase tracking-wider text-[10px] text-neutral-500 dark:text-neutral-400 mb-2'>
          {title}
        </div>
      )}
      <table className='w-full border-collapse'>
        <thead>
          <tr className='border-b border-neutral-200 dark:border-neutral-800'>
            <th className='text-left text-[10px] uppercase tracking-wide text-neutral-500 dark:text-neutral-400 pb-1 pr-3 font-semibold'>
              Monat
            </th>
            {metricKeys.map((key) => (
              <th key={key} className='text-right text-[10px] uppercase tracking-wide text-neutral-500 dark:text-neutral-400 pb-1 pl-3 font-semibold'>
                {formatSeasonalityKey(key, baseLabel)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map(({ period, metrics }) => (
            <tr key={period} className='border-b last:border-0 border-neutral-200 dark:border-neutral-800'>
              <td className='py-1 pr-3 text-neutral-700 dark:text-neutral-100 font-semibold whitespace-nowrap'>
                {formatSeasonalityPeriod(period)}
              </td>
              {metricKeys.map((key) => (
                <td key={key} className='py-1 pl-3 text-right text-neutral-700 dark:text-neutral-100 whitespace-nowrap'>
                  {metrics[key] !== undefined ? formatSeasonalityValue(metrics[key], key) : '–'}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </>
  )
})

const SeasonalityTooltipContent = memo(function SeasonalityTooltipContent({ datasets }) {
  return (
    <div className='text-xs leading-relaxed text-left max-h-72 overflow-y-auto p-2 bg-white text-neutral-800 dark:bg-neutral-900 dark:text-neutral-100 rounded-md shadow-lg min-w-[280px]'>
      {datasets.map((dataset, index) => (
        <div
          key={dataset.key ?? dataset.title ?? index}
          className={index ? 'pt-3 mt-3 border-t border-neutral-200 dark:border-neutral-800' : undefined}
        >
          <SeasonalityTooltipSection items={dataset.items} title={dataset.title} baseLabel={dataset.baseLabel} />
        </div>
      ))}
    </div>
  )
})

const Center = ({ children }) => <div className='flex items-center justify-center'>{children}</div>

export const SeasonalityCell = memo(function SeasonalityCell({ value }) {
  const datasets = useMemo(() => normalizeSeasonalityDatasets(Array.isArray(value) ? value : [value]), [value])
  const appendTo = useMemo(() => {
    if (typeof document === 'undefined') {
      return undefined
    }
    return () => document.body
  }, [])

  if (!datasets.length) {
    return <Center><Pill>–</Pill></Center>
  }

  const summaryLabel = computeSeasonalityStreak(datasets) ?? 'Seasonality'

  return (
    <Center>
      <Tippy
        placement='bottom'
        interactive
        maxWidth='none'
        appendTo={appendTo}
        zIndex={9999}
        content={<SeasonalityTooltipContent datasets={datasets} />}
      >
        <span>
          <Pill className='cursor-default text-xs font-semibold uppercase tracking-wide bg-neutral-200 text-neutral-700 dark:bg-neutral-900 dark:text-neutral-100'>
            {summaryLabel}
          </Pill>
        </span>
      </Tippy>
    </Center>
  )
})

export const VelocityPriceCell = memo(function VelocityPriceCell({ value }) {
  if (!value) {
    return <Center><Pill>–</Pill></Center>
  }

  return (
    <Center>
      <Pill>{value}</Pill>
    </Center>
  )
})
